import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';

import { AutoComplete } from 'primeng/autocomplete';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { intRegExPattern, SBaseDisplay, SDate } from '@summit/core';
import { DisplaySchema, isGuid, SummitDisposing, SummitIdObject, ViewField } from '@summit/models';
import { CrudCacheParameters } from '@summit/services';
import { ApiError, BlurDirective, LayoutService } from '@summit/ui';
import { Dayjs } from 'dayjs';
import { AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { FloatLabel } from 'primeng/floatlabel';
import { InputGroup } from 'primeng/inputgroup';
import { Skeleton } from 'primeng/skeleton';
import { SFormErrorInputComponent } from '../s-form-error-input';
import { AutocompleteLabelDirective } from './autocomplete-label-directive';
@Component({
  selector: 's-form-autocomplete-text',
  templateUrl: './s-form-autocomplete.component.html',
  styleUrls: ['./s-form-autocomplete.component.scss'],
  standalone: true,
  imports: [
    SFormErrorInputComponent,
    AutoCompleteModule,
    InputGroup,
    FloatLabel,
    ReactiveFormsModule,
    BlurDirective,
    AsyncPipe,
    NgTemplateOutlet,
    Skeleton,
    AutocompleteLabelDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormAutoCompleteComponent<T extends SummitIdObject, U extends SummitIdObject>
  extends SummitDisposing
  implements OnInit, AfterViewInit
{
  @Input() label: string;
  @Input() form: UntypedFormGroup;
  @Input() groupName: string;
  @Input() fieldName: keyof T | string;
  @Input() apiError: ApiError;
  @Input() schema: DisplaySchema<T> | false;
  @Input() items$: Observable<U[]>;
  @Input() cacheParameters: Observable<CrudCacheParameters>;
  @Input() defaultText: string;
  @Input() keyProperty: keyof U & string = 'id' as keyof U & string;
  @Input() displayProperty = 'name';
  @Input() customFilter: (item: U) => boolean;
  @Input() autoSelectEntry = true;
  @Input() showNewButton = false;
  @Input() disabled = false;
  @Input() displayHint: string;
  @Input() canCreate = false;
  @Input() readonly = false;
  @Input() showDriverToggle = false;
  @Input() multiple = false;
  @Input() perfectMatchTest: (search: string, item: U) => boolean; //__display is added to item
  @Output() addEvent = new EventEmitter<Event>();
  @Output() comboBlur = new EventEmitter<Event>();
  @Output() comboChange = new EventEmitter<string>();
  @ViewChild('input', { static: false }) input: AutoComplete;
  @ContentChild('prefix') prefix: TemplateRef<unknown>;
  @ContentChild('suffix') suffix: TemplateRef<unknown>;

  autoHighlight = false;
  filteredOptionsBs = new BehaviorSubject<{ id: string; __display: string }[]>([]);
  filteredOptions$: Observable<{ id: string; __display: string }[]> = this.filteredOptionsBs.asObservable();
  myControl: AbstractControl | null;
  //createObject = true;
  customFilterChanged$ = new BehaviorSubject<boolean>(null);
  loading$: Observable<boolean>;
  //defaultText$: Observable<string>;
  public itemsCache: Map<U[keyof U], U>;

  private keyboardSelectedOption: string | number; // unsure of use in prime
  attribute: ViewField<T> | undefined;
  createObject: boolean;
  selectionCache: U | U[];
  field: string;
  inputId: string;

  constructor(
    protected cd: ChangeDetectorRef,
    protected layout: LayoutService
  ) {
    super();
    this.field = this.getField.bind(this) as unknown as string;
    this.createObject = false;
  }

  ngOnInit() {
    this.inputId = this.fieldName?.toString() + this.groupName?.toString();

    //console.log(`${this.fieldName.toString()} has items$`, this.items$);

    this.items$?.pipe(takeUntil(this.disposing$)).subscribe((items) => {
      //console.log('Using Items: ', items)
      this.itemsCache = new Map(items.map((item) => [item[this.keyProperty], item]));
      //console.log('itemsCache', this.itemsCache);
    });
    //const debugging = false && this.fieldName == 'destNum';
    if (this.schema) {
      this.attribute = this.schema.getAttribute(this.fieldName as keyof T & string, this.groupName);
      if (!this.attribute) {
        console.error(
          `Could not find Property: ${this.fieldName?.toString()} in group ${this.groupName ?? 'root'} in Tree:`,
          this.schema
        );
      }
    }
    this.label = this.label ?? this.attribute?.displayName;
    this.defaultText = this.defaultText ?? this.label;
    this.displayHint = this.displayHint ?? this.attribute?.displayHint;
    if (this.form) {
      //* Access to control
      this.myControl = this.form.get(this.fieldName?.toString());
      //console.log(`got control from form`, this.myControl, this.form);

      this.myControl?.valueChanges.pipe(takeUntil(this.disposing$)).subscribe((value) => {
        if (this.multiple && Array.isArray(value)) {
          this.selectionCache = value.map((id) => this.itemsCache?.get(id)).filter((item) => item != null);
        }
      });

      //* Keep Filter List Up to date and selection in sync
      //console.log('items$', this.items$);
      //console.log('customFilterChanged$', this.customFilterChanged$);
      combineLatest([this.items$, this.customFilterChanged$])
        .pipe(
          // distinctUntilChanged(),
          map(([itemList, _filter]) => {
            //this.itemsCache = itemList; //to keep in sync since the subscription above isn't timely
            //console.log(`${this.fieldName.toString()} got new items`, itemList);
            return Array.isArray(itemList)
              ? itemList
                  .filter((item) => !this.customFilter || this.customFilter(item))
                  .map((item: Record<keyof U | '__display', unknown>) => {
                    item.__display = this.displayFn(item[this.keyProperty] as string | number);
                    if (item.__display == null) console.log('null display', item);
                    return item;
                  })
              : [];
          }),
          //tap((items) => console.log(`${this.fieldName?.toString()}: items`, items)),
          tap((items) => {
            const currentValue = this.myControl?.value;
            if ((currentValue || currentValue === 0) && !isGuid(currentValue)) {
              //console.log("Lookup update: value isn't a guid", currentValue);
              const currentObject = items.find((item) => item.__display == currentValue || item['id'] == currentValue);
              if (currentObject) {
                this.setValue(currentObject[this.keyProperty] as string | number, true);
              }
            }
          }),
          takeUntil(this.disposing$)
        )
        .subscribe((items: U[]) => {
          //this.itemsCache = items;
          const localValues = items.map((item) => {
            return {
              id: item[this.keyProperty].toString(),
              __display: item['__display'].toString()
            };
          });
          //console.log(`${this.fieldName?.toString()}: localValues`, localValues);
          this.filteredOptionsBs.next(localValues);
          this.setValue(this.myControl?.value);
        });
    } else {
      console.error(`No form found for ${this.fieldName?.toString()}`);
    }
    if (this.readonly) {
      this.myControl?.disable();
    }

    //this.autoInput.focusInput();
  }

  ngAfterViewInit() {
    // this.cacheParameters
    //   ?.pipe(
    //     map((m) => m?.isLoading),
    //     distinctUntilChanged(),
    //     takeUntil(this.disposing$)
    //   )
    //   .subscribe((isLoading) => {
    //     if (isLoading) {
    //       console.log('isLoading', isLoading);
    //       this.form.get(this.fieldName?.toString())?.disable();
    //     } else {
    //       this.form.get(this.fieldName?.toString())?.enable();
    //     }
    //   });
  }

  queryFilter(event) {
    //console.log('query Filter', event, this.itemsCache);

    if (event.query.length > 0) {
      const query = event?.query?.toLowerCase();
      const filteredList = [...this.itemsCache.values()].filter((item) => {
        return ((item['__display'] ?? item[this.displayProperty])?.toString().toLowerCase()?.indexOf(query) ?? -1) >= 0;
      });
      //console.log('filteredList', filteredList);
      this.filteredOptionsBs.next(
        filteredList.map((item) => {
          return {
            id: item[this.keyProperty].toString(),
            __display: (item['__display'] ?? item[this.displayProperty])?.toString()
          };
        })
      );
    } else {
      const local = this.itemsCache
        ? [...this.itemsCache.values()].map((item) => {
            return {
              id: item[this.keyProperty].toString(),
              __display: (item['__display'] ?? item[this.displayProperty])?.toString()
            };
          })
        : [];
      this.filteredOptionsBs.next(local);
    }
  }
  getField() {
    // console.log('getField', this.selectionCache);
    return Array.isArray(this.selectionCache)
      ? this.displayFn(this.selectionCache?.[0]?.id)
      : this.displayFn(this.selectionCache?.id);
  }
  displayFn(id: string | number | { __display: string } = null): string {
    //console.log(`${this.fieldName.toString()} Create display name for ${id}`, id);

    // Handle case where PrimeNG passes the full object with __display
    if (id && typeof id === 'object' && '__display' in id) {
      return id.__display;
    }

    // Handle case where PrimeNG passes the full cached item object
    if (id && typeof id === 'object' && this.displayProperty in (id as any)) {
      return this.getDisplayProperty(id as unknown as U) ?? '';
    }

    if (!id && id !== 0) {
      return '';
    }

    const displayItem = Array.from(this.itemsCache)?.find(
      ([itemId, item]) => itemId?.toString() === id?.toString() || itemId == id || item.id == id
    )?.[1];

    return this.getDisplayProperty(displayItem) ?? '';
  }

  getDisplayProperty(selectedItem: U): string | null {
    // console.log('getDisplayProperty', selectedItem);
    if (selectedItem == null || selectedItem == undefined) return null;
    if (this.displayProperty === 'toShortString') {
      return (selectedItem as unknown as SBaseDisplay)?.toShortString();
    }
    return selectedItem[this.displayProperty]?.toString() ?? null;
  }
  setValue(id: string[] | string | number, initialLoad = false) {
    // const newValue = this.items.find(entry => entry.id === id);
    //console.log(`${this.fieldName.toString()} Setting Value to id: ${id}`, id);
    if (this.multiple) {
      const addedValue = this.itemsCache?.get(id as U[keyof U]);
      if (!addedValue) return;
      // console.log(`${this.fieldName.toString()} Adding value to selectionCache`, addedValue);
      this.selectionCache = (this.myControl?.value ?? [])
        .map((id) => this.itemsCache?.get(id))
        .filter((item) => item != null) as U[];

      // Only add if not already in the cache
      const existingIndex = this.selectionCache.findIndex(
        (item) => item[this.keyProperty] === addedValue[this.keyProperty]
      );
      if (existingIndex === -1) {
        this.selectionCache.push(addedValue);
      }
      // console.log(`${this.fieldName.toString()} Updated selectionCache after adding`, this.selectionCache);

      // Remove duplicates
      this.selectionCache = this.selectionCache
        .filter((entry) => entry)
        .filter((entry, index, arr) => {
          return arr.findIndex((x) => x.id === entry.id) === index;
        });
      // console.log('Updated selectionCache', this.selectionCache);
      if (!this.selectionCache) return;
      this.createObject = false;
      // console.log(
      //   'Setting Value to:',
      //   this.selectionCache.map((entry) => entry[this.keyProperty])
      // );
      if (initialLoad) this.myControl?.patchValue(this.selectionCache.map((entry) => entry[this.keyProperty]));
      else {
        var newValue = this.selectionCache.map((entry) => entry[this.keyProperty]);
        if (JSON.stringify(this.myControl?.value) !== JSON.stringify(newValue)) {
          // console.log(`${this.fieldName.toString()} Updating control value to:`, newValue, this.myControl?.value);
          if (newValue.length === 1) {
            this.myControl?.setValue(newValue[0]);
          } else this.myControl?.setValue(newValue);
        }
      }
      this.myControl?.updateValueAndValidity();
      this.cd?.detectChanges();
      this.input?.cd?.detectChanges();
      this.comboChange.emit();
    } else {
      this.selectionCache = this.itemsCache?.get(id as U[keyof U]);
      //console.log('Setting Value to:', this.selectionCache?.[this.keyProperty], this.itemsCache);
      if (!this.selectionCache) return;
      this.createObject = false;
      if (initialLoad) this.myControl?.patchValue(this.selectionCache[this.keyProperty].toString());
      else this.myControl?.setValue(this.selectionCache[this.keyProperty].toString());
      this.myControl?.updateValueAndValidity();
      this.cd?.detectChanges();
      this.input?.cd?.detectChanges();
      this.comboChange.emit();
    }
    return;
  }
  isRequired(): boolean {
    return (
      (this.attribute &&
        this.attribute.validationMessages &&
        this.attribute.validationMessages.find(
          (entry) => entry.type === 'required'
          //|| entry.type.startsWith('required')
        ) != null) ??
      false
    );
  }

  async onShow() {
    const isMobile = await firstValueFrom(this.layout.mobile$);
    if (isMobile) {
      document.getElementById(this.inputId).blur();
    }
  }

  onClear() {
    // console.log('onClear');
    if (this.multiple) {
      this.selectionCache = [];
      this.myControl?.setValue([]);
    } else {
      this.myControl?.setValue('');
    }
    this.myControl?.updateValueAndValidity();
  }
  //onSelection(selection: U) {
  onSelection($event: AutoCompleteSelectEvent) {
    //console.log('onSelection', $event);
    const debugging = false;
    const selection = $event?.value;
    //const selection = $event?.value;
    if (!selection) return;
    //if (debugging)
    // console.log(`Selection changed to ${selection[this.keyProperty]} = ${selection?.['__display']}:`);
    this.keyboardSelectedOption = selection[this.keyProperty] as string | number;
    if (this.multiple) {
      //console.log(`${this.fieldName.toString()} onSelection`, selection, this.selectionCache);
      //don't touch the selection cache, let set value do it
    } else {
      this.selectionCache = selection;
    }
    this.setValue(this.keyboardSelectedOption);
  }
  disableOnBlurTil: Dayjs;
  onDropDownClick(_event) {
    //console.log('onDropDownClick', event);
    //event.originalEvent.preventDefault();
    // event.originalEvent.stopPropagation();
    if (this.input?.overlayVisible ?? false) {
      this.input.hide();
    } else {
      this.input.show();
      if (this.layout.isMobile) {
        this.disableOnBlurTil = SDate.newUtc().add(100, 'milliseconds');
        document.getElementById(this.inputId).blur();
      }
    }
  }
  onBlur(_event?) {
    //console.log('onBlur', _event);
    if (this.disableOnBlurTil && this.disableOnBlurTil.isAfter(SDate.newUtc())) return;
    var availOptionCount = this.filteredOptionsBs.value.length;
    //console.log('Available Option Count:', availOptionCount);
    if (availOptionCount == 1 && !isGuid(this.myControl?.value) && this.isRequired()) {
      //console.log('On Blur: Highlight Option', availOptionCount);
      const value = this.filteredOptionsBs.value[0];
      //      console.log('On Blur: Highlight Option', value);
      this.setValue(value.id.toString());
      this.input.hide();
      this.cd.detectChanges();
      return;
    } else {
      const intRegEx = new RegExp(intRegExPattern);
      //console.log('testing int', intRegEx.test(this.myControl?.value));
      if (intRegEx.test(this.myControl?.value)) {
        const perfectMatch = this.itemsCache.get(this.myControl?.value);
        if (perfectMatch) {
          // console.log('On Blur: Highlight Option', perfectMatch);
          this.setValue(perfectMatch[this.keyProperty] as string | number);
          this.input.hide();
          this.cd.detectChanges();
          return;
        }
      }
    }
    // console.log('On Blur: No perfect match found', this.myControl?.value);
    if (!isGuid(this.myControl?.value) && this.myControl?.value?.length > 0) {
      //console.log('On Blur: No perfect match found', this.myControl?.value);
      if (availOptionCount !== 1) {
        //console.log('On Blur: No perfect match found, doing custom search');
        if (this.perfectMatchTest) {
          const perfectMatch = [...this.itemsCache.values()].find((item) =>
            this.perfectMatchTest(this.myControl?.value, item)
          );
          if (perfectMatch) {
            //console.log('Perfect match found', perfectMatch);
            this.setValue(perfectMatch[this.keyProperty] as string | number);
            this.input.hide();
            this.cd.detectChanges();
            return;
          }
        } else {
          //console.log('Doing My own search', this.myControl?.value);
          var inputValue = this.myControl?.value;
          if (!inputValue) return;
          //console.log('inputValue', inputValue);
          inputValue = inputValue.toString().toUpperCase();
          const possibleValues = [...this.itemsCache.values()].filter((item) => {
            const testString = item['__display']?.toString();
            if (!testString || testString.length === 0) return false;
            return testString.toUpperCase().indexOf(inputValue.toUpperCase()) >= 0;
          });
          //console.log('Possible Values', possibleValues);
          if (possibleValues.length == 1) {
            this.setValue(possibleValues[0][this.keyProperty] as string | number);
            this.input.hide();
            this.cd.detectChanges();
            return;
          }
        }
      }
      //console.log('No perfect match found: Leaving value', this.myControl?.value);
      this.createObject = true;
    }
  }

  updateSelection(): void {
    const id = this.myControl?.value;
    this.setValue(id);
  }

  onKeyUp(event: KeyboardEvent) {
    //console.log('onKeyUp', event);
    if (event.key === 'Enter' || event.key === ';') {
      this.onBlur();
    }
  }

  /*
  below is not currently used within this
*/
  public async triggerAdd(params: Event) {
    // console.log(`add new entry with `, params);
    this.addEvent.emit(params);
  }
  onSelect(event: Event) {
    console.log('onSelect', event);
  }
  //Used in quick Create: Truck Alias
  getValue() {
    // console.log(`GetValue returning value of: ${this.myControl.value}`, this.myControl)
    return this.myControl?.value;
  }

  getDisplayValue(id: string) {
    // console.log(`GetDisplayValue returning value of: ${this.myControl.value}`, this.myControl)
    return this.displayFn(id);
  }

  getDisplayForOption(id: string | number): string {
    // This method is specifically for PrimeNG optionLabel
    // It should only get the display text for a given ID without complex object handling
    if (!id && id !== 0) return '';
    const item = this.itemsCache?.get(id as U[keyof U]);
    return this.getDisplayProperty(item) ?? '';
  }
}
